import { create } from 'zustand';
import type {
  LearningPath, NodeContent, CheckpointScore, GenerationStatus,
} from '../types/learning-path';
import {
  loadLearningPath, loadNodeContent, updateProgress,
  submitCheckpoint as submitCheckpointService, onGenerationProgress,
  computeOverallMastery, CHECKPOINT_PASS_THRESHOLD, CHECKPOINT_PERFECT_THRESHOLD,
} from '../services/learningPath';
import { mutateCounters, recordGamificationAction } from '../services/gamification';
import { upsertReviewAfterCheckpoint } from '../services/review';
import { useAuthStore } from './auth-store';

function tierAllowsFreezes(): boolean {
  const tier = useAuthStore.getState().user?.subscription;
  return tier === 'pro' || tier === 'teams';
}

function currentUserId(): string | null {
  return useAuthStore.getState().user?.uid ?? null;
}

interface LearningStore {
  currentPath: LearningPath | null;
  currentNodeId: string | null;
  currentNodeContent: NodeContent | null;
  generationStatus: GenerationStatus | null;
  generationProgress: { totalNodes: number; completedNodes: number } | null;
  loading: boolean;

  loadPath: (pathId: string) => Promise<void>;
  setPath: (path: LearningPath) => void;
  navigateToNode: (nodeId: string) => Promise<void>;
  completeNode: (nodeId: string) => Promise<void>;
  submitCheckpoint: (nodeId: string, answer: string, timeTaken?: number) => Promise<CheckpointScore>;
  listenToGeneration: (pathId: string) => () => void;
  reset: () => void;
}

export const useLearningStore = create<LearningStore>((set, get) => ({
  currentPath: null,
  currentNodeId: null,
  currentNodeContent: null,
  generationStatus: null,
  generationProgress: null,
  loading: false,

  loadPath: async (pathId) => {
    set({ loading: true });
    const path = await loadLearningPath(pathId);
    set({
      currentPath: path,
      generationStatus: path?.generationStatus ?? null,
      generationProgress: path?.generationProgress ?? null,
      loading: false,
    });
  },

  // Synchronously hydrate from a cached path without a Firestore fetch.
  // Used by LearningPage's IndexedDB fast-path; the real loadPath still
  // runs in parallel to refresh the data.
  setPath: (path) => set({
    currentPath: path,
    generationStatus: path.generationStatus,
    generationProgress: path.generationProgress ?? null,
    loading: false,
  }),

  navigateToNode: async (nodeId) => {
    const { currentPath } = get();
    if (!currentPath) return;
    set({ currentNodeId: nodeId, currentNodeContent: null, loading: true });
    const content = await loadNodeContent(currentPath.pathId, nodeId);
    set({ currentNodeContent: content, loading: false });
    await updateProgress(currentPath.pathId, nodeId, 'visit');
  },

  completeNode: async (nodeId) => {
    const { currentPath } = get();
    if (!currentPath) return;
    if (currentPath.progress.completedNodes.includes(nodeId)) {
      // Already completed — don't re-emit gamification.
      return;
    }
    await updateProgress(currentPath.pathId, nodeId, 'complete');

    let isPathComplete = false;
    set((state) => {
      if (!state.currentPath) return state;
      const completed = new Set(state.currentPath.progress.completedNodes);
      completed.add(nodeId);
      isPathComplete = completed.size >= state.currentPath.nodes.length;
      return {
        currentPath: {
          ...state.currentPath,
          progress: {
            ...state.currentPath.progress,
            completedNodes: Array.from(completed),
          },
        },
      };
    });

    const userId = currentUserId();
    if (userId) {
      void recordGamificationAction(userId, 'node_completed', {
        counterMutations: { totalNodesCompleted: 1 },
        tickStreak: true,
        tierAllowsFreezes: tierAllowsFreezes(),
      });
      if (isPathComplete) {
        void recordGamificationAction(userId, 'path_completed', {
          counterMutations: { pathsCompleted: 1 },
        });
      }
    }
  },

  submitCheckpoint: async (nodeId, answer, timeTaken) => {
    const { currentPath, currentNodeContent } = get();
    if (!currentPath || !currentNodeContent) {
      throw new Error('No active path or node');
    }

    const checkpointData = currentNodeContent.content.checkpointData;
    if (!checkpointData) throw new Error('Node has no checkpoint data');

    const existingScores = currentPath.progress.checkpointScores;
    const prevAttempts = existingScores[nodeId]?.attempts ?? 0;

    const score = await submitCheckpointService(
      currentPath.pathId,
      nodeId,
      answer,
      {
        question: checkpointData.question,
        questionType: checkpointData.questionType,
        options: checkpointData.options,
        correctAnswer: checkpointData.correctAnswer,
        rubric: checkpointData.rubric,
        conceptsTested: checkpointData.conceptsTested,
      },
      { timeTaken, attempts: prevAttempts + 1, existingScores },
    );

    // Update local state (scores + rolled-up mastery)
    set((state) => {
      if (!state.currentPath) return state;
      const checkpointScores = {
        ...state.currentPath.progress.checkpointScores,
        [nodeId]: score,
      };
      return {
        currentPath: {
          ...state.currentPath,
          progress: {
            ...state.currentPath.progress,
            checkpointScores,
            overallMastery: computeOverallMastery(checkpointScores),
          },
        },
      };
    });

    const userId = currentUserId();
    if (userId && typeof score.score === 'number') {
      // Schedule the spaced review for this node (fire-and-forget).
      void upsertReviewAfterCheckpoint({
        userId,
        contentId: currentPath.contentId,
        pathId: currentPath.pathId,
        nodeId,
        conceptIds: checkpointData.conceptsTested ?? [],
        label: checkpointData.question,
        score: score.score,
      }).catch(() => {});

      const passed = score.score >= CHECKPOINT_PASS_THRESHOLD;
      const perfect = score.score >= CHECKPOINT_PERFECT_THRESHOLD;
      if (perfect) {
        void recordGamificationAction(userId, 'checkpoint_perfect', {
          counterMutations: {
            checkpointsPassedCount: 1,
            consecutivePerfectCheckpoints: 1,
          },
          tickStreak: true,
          tierAllowsFreezes: tierAllowsFreezes(),
        });
      } else if (passed) {
        void recordGamificationAction(userId, 'checkpoint_passed', {
          counterMutations: {
            checkpointsPassedCount: 1,
            consecutivePerfectCheckpointsAbsolute: 0,
          },
          tickStreak: true,
          tierAllowsFreezes: tierAllowsFreezes(),
        });
      } else {
        // Failed checkpoint — perfect-run streak resets, no XP awarded.
        void mutateCounters(userId, { consecutivePerfectCheckpointsAbsolute: 0 });
      }
    }

    return score;
  },

  listenToGeneration: (pathId) => {
    return onGenerationProgress(pathId, (status, progress) => {
      set({ generationStatus: status, generationProgress: progress });
      // Auto-reload path when complete
      if (status === 'complete') {
        get().loadPath(pathId);
      }
    });
  },

  reset: () => set({
    currentPath: null,
    currentNodeId: null,
    currentNodeContent: null,
    generationStatus: null,
    generationProgress: null,
    loading: false,
  }),
}));
